import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Microscope, RefreshCw, Trophy } from "lucide-react";

const PHASE_LABEL = {
  // Pre-departure
  checkin_announced: "Incheckning annonserad", checkin_changed: "Incheckning ändrad",
  gate_first: "Gate annonserad", gate: "Gate-byte", terminal: "Terminal-byte",
  gate_action_G: "Gå till gate", gate_action_B: "Påstigning",
  gate_action_F: "Sista utrop", gate_action_C: "Gate stängd",
  boarding: "Boarding", final_call: "Final call",
  // Avgång
  taxiing: "Taxning", wheels_up: "Wheels-up", departed: "Avgick",
  // Cruise / approach
  descent: "Sjunker", final_approach: "Slutlig inflygning",
  // Landning + post
  actual_landing: "Landad", baggage_belt: "Bagageband",
  // Avvikelser
  delayed: "Försenad", time_change: "Tidsändring",
  cancelled: "Inställd", diverted: "Omdirigerad", status: "Statusbyte",
  // Nödsignaler (highlightas röd i UI)
  squawk_7500: "🆘 Flygkapning (7500)",
  squawk_7600: "🆘 Radiokontakt förlorad (7600)",
  squawk_7700: "🆘 Nödsituation (7700)",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      month: "numeric", day: "numeric",
    });
  } catch { return iso; }
}

export function FlightForensicsPanel() {
  const [priorityTable, setPriorityTable] = useState([]);
  const [watches, setWatches] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadTable = useCallback(async () => {
    try {
      const { data } = await api.get("/flights/source-priority");
      setPriorityTable(data?.items || []);
    } catch { /* tyst */ }
  }, []);

  const loadWatches = useCallback(async () => {
    try {
      const { data } = await api.get("/flights/watched");
      // Bara landade watches
      const landed = (data?.items || []).filter(
        (w) => (w.last_status || {}).actual_landing_utc,
      );
      setWatches(landed);
    } catch { /* tyst */ }
  }, []);

  const runAnalysis = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/flights/${id}/forensics`);
      setAnalysis(data);
    } catch (err) {
      setAnalysis({ ok: false, error: err?.message || "Okänt fel" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTable();
    loadWatches();
  }, [loadTable, loadWatches]);

  return (
    <div className="card-soft p-3 space-y-3" data-testid="flight-forensics-panel">
      <div className="flex items-center gap-2">
        <Microscope size={14} className="text-[color:var(--moss)]" />
        <h3 className="text-sm font-semibold">Forensics — flyg-analys</h3>
      </div>

      {/* Priority-table */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1 flex items-center gap-1">
          <Trophy size={10} /> Empirisk källprioritet (aggregerat)
        </p>
        {priorityTable.length === 0 ? (
          <p className="text-xs text-[color:var(--text-tertiary)] italic">
            Inga data än — bygger efter första landningen.
          </p>
        ) : (
          <div className="text-[11px] font-mono space-y-0.5">
            {priorityTable.map((row, i) => (
              <div key={i} className="flex items-baseline gap-2">
                <span className="w-32 truncate text-[color:var(--text-secondary)]">
                  {PHASE_LABEL[row.phase] || row.phase}
                </span>
                <span className="font-semibold">{row.source}</span>
                <span className="text-[color:var(--text-tertiary)]">
                  {row.first_count}/{row.total_seen} ({Math.round(row.first_rate * 100)}%)
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-flygnings analys */}
      <div className="pt-2 border-t border-[color:var(--border-subtle)]">
        <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1">
          Per-flygnings rapport
        </p>
        <div className="flex items-center gap-2">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="flex-1 text-xs h-7 px-2 rounded-md border border-[color:var(--border-subtle)] bg-white"
            data-testid="forensics-select-watch"
          >
            <option value="">Välj landad flygning ...</option>
            {watches.map((w) => (
              <option key={w.id} value={w.id}>
                {w.flight_id} ({w.date})
              </option>
            ))}
          </select>
          <Button
            size="sm"
            className="h-7 px-2 bg-[color:var(--moss)] hover:bg-[#1f421f]"
            disabled={!selectedId || loading}
            onClick={() => runAnalysis(selectedId)}
            data-testid="forensics-run"
          >
            {loading ? <RefreshCw size={12} className="animate-spin" /> : "Analysera"}
          </Button>
        </div>

        {analysis?.ok && (
          <div className="mt-2 text-[11px] font-mono space-y-1" data-testid="forensics-result">
            <p>
              <span className="font-semibold">{analysis.flight_id}</span>
              <span className="text-[color:var(--text-tertiary)]"> · {analysis.total_pushes} pushar · {analysis.total_fetch_events} fetch-events</span>
            </p>
            {Object.entries(analysis.phases || {}).map(([phase, info]) => {
              const seen = info.first_seen;
              const pushed = info.push_sent;
              if (!seen && !pushed) return null;
              const isEmergency = phase.startsWith("squawk_");
              return (
                <div
                  key={phase}
                  className={`flex flex-wrap gap-x-2 items-baseline ${isEmergency ? "bg-red-50 border-l-2 border-red-600 pl-2 py-0.5 rounded-r" : ""}`}
                  data-testid={`forensics-phase-${phase}`}
                >
                  <span className={`w-28 truncate ${isEmergency ? "text-red-700 font-bold" : "text-[color:var(--text-secondary)]"}`}>
                    {PHASE_LABEL[phase] || phase}
                  </span>
                  {seen ? (
                    <span className="text-emerald-700">
                      {seen.source} @ {fmtTime(seen.ts)}
                    </span>
                  ) : (
                    <span className="text-[color:var(--text-tertiary)]">first_seen=—</span>
                  )}
                  {pushed ? (
                    <span className="text-blue-700">→ push {fmtTime(pushed.ts)}</span>
                  ) : (
                    <span className="text-red-600 font-semibold">MISSED</span>
                  )}
                  {info.latency_ms != null && (
                    <span className="text-[color:var(--text-tertiary)]">{info.latency_ms}ms</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {analysis && !analysis.ok && (
          <p className="text-xs text-red-600 mt-2">{analysis.error || "Okänt fel"}</p>
        )}
      </div>
    </div>
  );
}
